import * as React from "react";

import NumberFlow from "@number-flow/react";
import {motion} from "framer-motion";

import {Icon} from "@client/components/Icon";
import {NavigationTitle} from "@client/components/NavigationTitle";
import {TouchHandler} from "@client/components/TouchHandler";
import {Locator} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";

interface Props {
    highlight: boolean;
    toggleMenu: () => void;
    isMenuOpen: boolean;
    isLoading: boolean;
    title: string;
    children?: React.ReactNode;
    more?: React.ReactNode;
    modal?: boolean;
    back:
        | {locator: Locator | {linkFromAppToSite: string}; title: string}
        | {title: string; onClick: () => void}
        | null;
}

export function Streak({value}: {value: number}) {
    const isGrayscale = value === 0;

    return (
        <motion.button
            layout
            className="
                relative flex h-10 items-center justify-center gap-1 rounded-full
                bg-white px-3.5 pt-0.5
            "
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                className={`
                    mb-0.5 size-5 transition-all duration-700
                    ${isGrayscale ? "grayscale" : "grayscale-0"}
                `}
            >
                <desc>
                    {"\n    Matches Fire Streamline Icon: https://streamlinehq.com\n  "}
                </desc>
                <path
                    fill="#ffef5e"
                    d="M21.418 13.944a9.426 9.426 0 0 1 -18.84 0.505c-0.01 -0.168 -0.01 -0.337 -0.01 -0.505A9.382 9.382 0 0 1 5.94 6.72a9.28 9.28 0 0 0 1.865 3.423 9.402 9.402 0 0 0 1.21 -4.633A10.078 10.078 0 0 0 7.708 0.55a12.456 12.456 0 0 1 10.239 12.153c0.001 0.087 -0.002 0.173 -0.01 0.258a9.062 9.062 0 0 0 3.085 -1.716c0.264 0.875 0.398 1.784 0.397 2.699Z"
                    strokeWidth={1}
                />
                <path
                    fill="#ff808c"
                    d="M12.638 12.455a0.248 0.248 0 0 1 0.29 0.381 4.16 4.16 0 0 0 -0.311 4.407 2.64 2.64 0 0 0 0.963 -1.248 0.245 0.245 0 0 1 0.137 -0.156 0.249 0.249 0 0 1 0.21 0A3.67 3.67 0 0 1 15.96 19.4a3.89 3.89 0 0 1 -3.968 3.969 3.987 3.987 0 0 1 -3.969 -4.08 7.495 7.495 0 0 1 4.614 -6.834Z"
                    strokeWidth={1}
                />
                <path
                    stroke="#191919"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.432 13.944a9.426 9.426 0 0 1 -18.84 0.505c-0.01 -0.168 -0.01 -0.337 -0.01 -0.505A9.382 9.382 0 0 1 5.955 6.72a9.267 9.267 0 0 0 1.865 3.423A9.402 9.402 0 0 0 9.03 5.51 10.079 10.079 0 0 0 7.72 0.55a12.456 12.456 0 0 1 10.24 12.153c0.001 0.087 -0.002 0.173 -0.01 0.258a9.065 9.065 0 0 0 3.084 -1.716c0.265 0.875 0.4 1.784 0.398 2.699v0Z"
                    strokeWidth={1}
                />
                <path
                    stroke="#191919"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12.652 12.455a0.248 0.248 0 0 1 0.288 0.381 4.16 4.16 0 0 0 -0.31 4.407 2.63 2.63 0 0 0 0.963 -1.248 0.247 0.247 0 0 1 0.242 -0.178c0.036 0 0.072 0.009 0.104 0.024a3.67 3.67 0 0 1 2.036 3.56 3.89 3.89 0 0 1 -3.968 3.968 3.987 3.987 0 0 1 -3.969 -4.08 7.495 7.495 0 0 1 4.614 -6.834v0Z"
                    strokeWidth={1}
                />
            </svg>
            <span className="text-base font-normal tracking-tighter">
                <NumberFlow value={value} />
            </span>
        </motion.button>
    );
}

export const MenuIcon = (props: {isMenuOpen: boolean; toggleMenu: () => void}) => {
    return (
        <button
            className="
                relative flex size-10 items-center justify-center rounded-full bg-white
                text-black
                dark:bg-slate-800 dark:text-slate-100
            "
            aria-label="Menu"
            onClick={() => {
                void hardware.haptic();
                props.toggleMenu();
            }}
        >
            <svg
                width="15"
                height="11"
                viewBox="0 0 15 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                {/* Top bar */}
                <motion.path
                    d="M1.54908 0.565063H6.62844C6.89787 0.565063 7.15626 0.672093 7.34677 0.862606C7.53729 1.05312 7.64431 1.31151 7.64431 1.58094C7.64431 1.85036 7.53729 2.10875 7.34677 2.29927C7.15626 2.48978 6.89787 2.59681 6.62844 2.59681H1.54908C1.27965 2.59681 1.02126 2.48978 0.830745 2.29927C0.640232 2.10875 0.533203 1.85036 0.533203 1.58094C0.533203 1.31151 0.640232 1.05312 0.830745 0.862606C1.02126 0.672093 1.27965 0.565063 1.54908 0.565063Z"
                    fill="currentColor"
                    animate={{
                        scale: props.isMenuOpen ? 0 : 1,
                        rotate: props.isMenuOpen ? -90 : 0,
                    }}
                    transition={{duration: 0.2, ease: "easeInOut"}}
                />

                {/* Bottom bar */}
                <motion.path
                    d="M8.66019 8.69205H13.7396C14.009 8.69205 14.2674 8.79908 14.4579 8.98959C14.6484 9.1801 14.7554 9.43849 14.7554 9.70792C14.7554 9.97735 14.6484 10.2357 14.4579 10.4263C14.2674 10.6168 14.009 10.7238 13.7396 10.7238H8.66019C8.39076 10.7238 8.13237 10.6168 7.94186 10.4263C7.75134 10.2357 7.64431 9.97735 7.64431 9.70792C7.64431 9.43849 7.75134 9.1801 7.94186 8.98959C8.13237 8.79908 8.39076 8.69205 8.66019 8.69205Z"
                    fill="currentColor"
                    animate={{
                        scale: props.isMenuOpen ? 0 : 1,
                        rotate: props.isMenuOpen ? -90 : 0,
                    }}
                    transition={{duration: 0.2, delay: 0.1, ease: "easeInOut"}}
                />

                {/* Middle bar */}
                <motion.path
                    d="M1.54908 4.62856H13.7396C14.009 4.62856 14.2674 4.73559 14.4579 4.9261C14.6484 5.11661 14.7554 5.375 14.7554 5.64443C14.7554 5.91386 14.6484 6.17225 14.4579 6.36276C14.2674 6.55327 14.009 6.6603 13.7396 6.6603H1.54908C1.27965 6.6603 1.02126 6.55327 0.830745 6.36276C0.640232 6.17225 0.533203 5.91386 0.533203 5.64443C0.533203 5.375 0.640232 5.11661 0.830745 4.9261C1.02126 4.73559 1.27965 4.62856 1.54908 4.62856Z"
                    fill="currentColor"
                    animate={{
                        scale: props.isMenuOpen ? 0 : 1,
                        rotate: props.isMenuOpen ? -90 : 0,
                    }}
                    transition={{duration: 0.2, delay: 0.2, ease: "easeInOut"}}
                />
            </svg>
        </button>
    );
};

export const Navigation = (props: Props) => {
    const loadingIcon = (
        <motion.div
            className="
                size-5 rounded-full border-4 border-stone-300 border-t-indigo-700
                will-change-transform
            "
            animate={{rotate: 360}}
            transition={{
                duration: 1,
                repeat: Infinity,
                ease: "linear",
            }}
        />
    );
    return (
        <div className="navigation-safe flex items-center bg-stone-50 px-4 pb-2.5">
            <title>{`${props.title} | Joy`}</title>

            <div className="flex-1">
                {props.back != null ? (
                    <TouchHandler
                        highlight={props.highlight}
                        className="flex items-center gap-0.5"
                        disableBackground
                        link={
                            "onClick" in props.back
                                ? props.back.onClick
                                : props.back.locator
                        }
                    >
                        <Icon large="medium" icon="custom-chevron-left" />
                        <span className="ml-0.5 text-sm font-normal">
                            {props.back.title}
                        </span>
                    </TouchHandler>
                ) : (
                    <MenuIcon
                        isMenuOpen={props.isMenuOpen}
                        toggleMenu={props.toggleMenu}
                    />
                )}
            </div>
            <div>
                {props.children == null ? (
                    <NavigationTitle title={props.title} />
                ) : (
                    props.children
                )}
            </div>
            <div className="flex flex-1 justify-end">
                {props.isLoading == true && loadingIcon}
                {props.isLoading == false && props.more}
            </div>
        </div>
    );
};
